import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import Visibility from '@mui/icons-material/Visibility';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import Edit from '@mui/icons-material/Edit';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { get, patch, put, del } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import {
  buildZelleOrdersSnapshot,
  saveZelleOrdersSnapshotToSession,
  readZelleInitialFilterState,
} from '../utils/zelleOrdersPersist';
function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(iso);
  }
}

function formatAmount(cents) {
  if (cents == null || cents === '') return '—';
  const n = Number(cents) / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
}

function extractCommentOrderIds(row) {
  const explicit = Array.isArray(row?.comment_order_ids)
    ? row.comment_order_ids
    : Array.isArray(row?.commentOrderIds)
      ? row.commentOrderIds
      : [];
  const fromExplicit = explicit
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0);
  if (fromExplicit.length) return [...new Set(fromExplicit)];

  const text = String(row?.comments || '');
  const matches = [...text.matchAll(/#(\d{2,})/g)];
  return [...new Set(matches.map((m) => Number(m[1])).filter((v) => Number.isFinite(v) && v > 0))];
}

function isOrderNotFoundInWc(err) {
  const d = err?.data || {};
  const msg = String(err?.message || '');
  return err?.status === 404 && (d?.code === 'ORDER_NOT_FOUND_IN_WC' || /not exist in WooCommerce|Order not found in WooCommerce/i.test(msg));
}

const SOURCE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'kiosk', label: 'Kiosk' },
  { value: 'non_kiosk', label: 'Non-kiosk' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
];

export default function ZellePaymentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { staff } = useAuth();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({ status: '', woo_order_id: '' });
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateRows, setDuplicateRows] = useState([]);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');
  const [duplicateDeletingOrderId, setDuplicateDeletingOrderId] = useState(null);
  const [duplicateDeleteCandidate, setDuplicateDeleteCandidate] = useState(null);

  const perms = Array.isArray(staff?.permissions) ? staff.permissions : [];
  const isAdminPermission = perms.includes('admin:full') || String(staff?.role || '').toLowerCase() === 'admin';
  const canDeleteOrders = isAdminPermission || perms.includes('orders:delete');

  // Filters (initial values restored from session when returning from /orders/:id/edit)
  const zelleFilterInitRef = React.useRef(null);
  if (zelleFilterInitRef.current === null) {
    zelleFilterInitRef.current = readZelleInitialFilterState();
  }
  const zf0 = zelleFilterInitRef.current;

  const [source, setSource] = useState(zf0.source);
  const [statusFilter, setStatusFilter] = useState(zf0.statusFilter);
  const [memoSearch, setMemoSearch] = useState(zf0.memoSearch);
  const [orderIdSearch, setOrderIdSearch] = useState(zf0.orderIdSearch);
  const [dateFromFilter, setDateFromFilter] = useState(zf0.dateFromFilter);
  const [dateToFilter, setDateToFilter] = useState(zf0.dateToFilter);
  const [searchText, setSearchText] = useState(zf0.searchText);
  const [page, setPage] = useState(zf0.page);
  const limit = 50;

  const memoSearchDebounceRef = React.useRef(null);
  useEffect(() => {
    if (memoSearchDebounceRef.current) clearTimeout(memoSearchDebounceRef.current);
    memoSearchDebounceRef.current = setTimeout(() => setMemoSearch(searchText.trim()), 300);
    return () => { if (memoSearchDebounceRef.current) clearTimeout(memoSearchDebounceRef.current); };
  }, [searchText]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit, offset: page * limit };
      if (source) params.source = source;
      if (statusFilter) params.status = statusFilter;
      if (memoSearch.trim()) params.search = memoSearch.trim();
      if (orderIdSearch.trim()) params.woo_order_id = orderIdSearch.trim();
      if (dateFromFilter) params.date_from = dateFromFilter;
      if (dateToFilter) params.date_to = dateToFilter;
      const data = await get('/api/zelle/admin/orders', params);
      const list = Array.isArray(data?.orders) ? data.orders : (Array.isArray(data?.data) ? data.data : []);
      setOrders(list);
      const rawTotal =
        data?.total ??
        data?.meta?.total ??
        data?.count ??
        data?.total_count ??
        data?.pagination?.total;
      const parsedTotal = Number(rawTotal);
      const normalizedTotal = Number.isFinite(parsedTotal) && parsedTotal >= 0 ? parsedTotal : null;
      setTotal(normalizedTotal ?? list.length);
      const nextFlag =
        Boolean(data?.next_page_token) ||
        Boolean(data?.nextPageToken) ||
        Boolean(data?.has_more) ||
        Boolean(data?.hasMore);
      const fallbackHasNext = normalizedTotal != null
        ? ((page + 1) * limit < normalizedTotal)
        : (list.length >= limit);
      setHasNextPage(nextFlag || fallbackHasNext);
      saveZelleOrdersSnapshotToSession(buildZelleOrdersSnapshot({
        source,
        statusFilter,
        searchText,
        memoSearch,
        orderIdSearch,
        dateFromFilter,
        dateToFilter,
        page,
      }));
    } catch (e) {
      const msg = e?.message || 'Failed to load Zelle orders';
      const isAuthError = e?.status === 401 || /unauthorized|session expired|session idle/i.test(String(msg));
      setSessionExpired(!!isAuthError);
      setError(isAuthError ? 'Session expired. Please log in again.' : msg);
      setOrders([]);
      setTotal(0);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  }, [page, source, statusFilter, memoSearch, orderIdSearch, dateFromFilter, dateToFilter]);

  const loadDuplicateOrders = useCallback(async () => {
    setDuplicateLoading(true);
    setDuplicateError('');
    try {
      const data = await get('/api/zelle/admin/duplicate-orders');
      const list = Array.isArray(data?.duplicates) ? data.duplicates : [];
      setDuplicateRows(list);
    } catch (e) {
      setDuplicateError(e?.message || 'Failed to load duplicate orders');
      setDuplicateRows([]);
    } finally {
      setDuplicateLoading(false);
    }
  }, []);

  const persistZelleFiltersBeforeLeave = useCallback(() => {
    saveZelleOrdersSnapshotToSession(buildZelleOrdersSnapshot({
      source,
      statusFilter,
      searchText,
      memoSearch,
      orderIdSearch,
      dateFromFilter,
      dateToFilter,
      page,
    }));
  }, [source, statusFilter, searchText, memoSearch, orderIdSearch, dateFromFilter, dateToFilter, page]);

  const openWooOrderEdit = useCallback((rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id) || id <= 0) return;
    persistZelleFiltersBeforeLeave();
    navigate(`/orders/${id}/edit`, { state: { returnTo: `${location.pathname}${location.search}` } });
  }, [navigate, location.pathname, location.search, persistZelleFiltersBeforeLeave]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!location.state?.orderEditSaved) return;
    loadOrders();
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.state?.orderEditSaved, loadOrders, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (editRow) {
      setEditError('');
      setEditSuccess('');
      setEditForm({
        status: editRow.status ?? '',
        woo_order_id: (editRow.wooOrderId ?? editRow.woo_order_id) != null ? String(editRow.wooOrderId ?? editRow.woo_order_id) : '',
      });
    }
  }, [editRow]);

  const handleOpenDuplicateDialog = async () => {
    setDuplicateDialogOpen(true);
    await loadDuplicateOrders();
  };

  const handleCloseDuplicateDialog = () => {
    if (duplicateDeletingOrderId) return;
    setDuplicateDialogOpen(false);
    setDuplicateError('');
  };

  const openView = (row) => setViewRow(row);
  const closeView = () => setViewRow(null);

  const openEditDialog = (row) => {
    setEditError('');
    setEditSuccess('');
    setEditRow(row);
  };
  const closeEditDialog = () => {
    if (!editLoading) {
      setEditError('');
      setEditSuccess('');
      setEditRow(null);
    }
  };

  const handleEditSubmit = async () => {
    if (!editRow) return;
    setEditLoading(true);
    setEditError('');
    setEditSuccess('');
    try {
      const updated = await patch(`/api/zelle/admin/orders/${editRow.id}`, {
        status: editForm.status.trim() || undefined,
        woo_order_id: editForm.woo_order_id.trim() ? editForm.woo_order_id.trim() : undefined,
      });
      setEditRow((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ...(updated || {}),
          status: updated?.status ?? editForm.status,
          woo_order_id: updated?.woo_order_id ?? (editForm.woo_order_id.trim() || null),
        };
      });
      setEditSuccess('Zelle payment updated successfully.');
      await loadOrders();
      closeView();
    } catch (e) {
      setEditError(e?.message || 'Update failed');
    } finally {
      setEditLoading(false);
    }
  };

  const hasWooOrder = (row) => {
    const id = row.wooOrderId ?? row.woo_order_id;
    return id != null && id !== '';
  };
  const isPendingOrCancelled = (row) => {
    const s = String(row?.status || '').toLowerCase();
    return s === 'pending' || s === 'cancelled' || s === 'canceled';
  };
  const canDeleteRow = (row) => {
    const sourceValue = String(row?.source || '').toLowerCase();
    return sourceValue === 'kiosk' && isPendingOrCancelled(row);
  };
  const handleDeleteRow = async (row) => {
    if (!canDeleteRow(row)) return;
    const ok = window.confirm(`Delete Zelle row #${row.id}? This cannot be undone.`);
    if (!ok) return;
    setDeletingId(row.id);
    try {
      await del(`/api/zelle/admin/orders/${row.id}`);
      await loadOrders();
      if (viewRow?.id === row.id) setViewRow(null);
    } catch (e) {
      alert(e?.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };
  const handleDeleteDuplicateOrder = async (row) => {
    setDuplicateDeleteCandidate(row);
  };

  const handleCloseDuplicateDeleteConfirm = () => {
    if (duplicateDeletingOrderId) return;
    setDuplicateDeleteCandidate(null);
  };

  const handleConfirmDeleteDuplicateOrder = async () => {
    const row = duplicateDeleteCandidate;
    const duplicateOrderId = Number(row?.duplicate_order_id);
    if (!Number.isFinite(duplicateOrderId) || duplicateOrderId <= 0) return;

    setDuplicateDeletingOrderId(duplicateOrderId);
    try {
      await put(`/api/orders/${duplicateOrderId}/cancel`, {
        suppress_email: true,
        internal_note: `Deleted by ${staff?.name || staff?.email || 'staff user'} due to duplicate order. Original order #${row.original_order_id}.`,
      });
      await loadDuplicateOrders();
      await loadOrders();
      setDuplicateDeleteCandidate(null);
    } catch (e) {
      try {
        if (isOrderNotFoundInWc(e)) {
          await del(`/api/orders/${duplicateOrderId}/local`);
          await loadDuplicateOrders();
          await loadOrders();
          setDuplicateDeleteCandidate(null);
          return;
        }
      } catch (localErr) {
        alert(localErr?.message || 'Failed to delete duplicate order locally');
        return;
      }
      alert(e?.message || 'Failed to trash duplicate order');
    } finally {
      setDuplicateDeletingOrderId(null);
    }
  };

  /** Hide Comments + Actions when Source filter is Kiosk — users only scan rows; WC Order still links to Woo. */
  const showCommentsAndActions = source !== 'kiosk';

  return (
    <>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Zelle Payments
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        List all Zelle orders from kiosk_zelle_orders. Filter by source, status, sent-by or memo text, date range, or order ID.
      </Typography>

      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<Search />} onClick={handleOpenDuplicateDialog}>
          Duplicate Orders
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Filters & search</Typography>
        <Stack spacing={2}>
          <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={1.5} alignItems="center" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={source}
                label="Source"
                displayEmpty
                renderValue={(value) => {
                  const selected = SOURCE_OPTIONS.find((o) => o.value === value);
                  return selected ? selected.label : 'All';
                }}
                onChange={(e) => setSource(e.target.value)}
              >
                {SOURCE_OPTIONS.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                displayEmpty
                renderValue={(value) => {
                  const selected = STATUS_OPTIONS.find((o) => o.value === value);
                  return selected ? selected.label : 'All';
                }}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Sent by / Memo"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search sent-by or memo text"
              sx={{ minWidth: 200, flex: '1 1 220px', maxWidth: 420 }}
            />
            <TextField
              size="small"
              label="Order ID"
              value={orderIdSearch}
              onChange={(e) => setOrderIdSearch(e.target.value)}
              placeholder="WooCommerce order ID"
              sx={{ width: 140 }}
            />
            <Button variant="outlined" startIcon={<Search />} onClick={() => setPage(0)}>
              Apply
            </Button>
          </Stack>
          <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={1.5} alignItems="center" useFlexGap>
            <TextField
              size="small"
              label="From"
              type="date"
              value={dateFromFilter}
              onChange={(e) => setDateFromFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            <TextField
              size="small"
              label="To"
              type="date"
              value={dateToFilter}
              onChange={(e) => setDateToFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography color="error" variant="body2">{error}</Typography>
          {sessionExpired && (
            <Button size="small" variant="contained" onClick={() => navigate('/login')}>
              Log in again
            </Button>
          )}
        </Stack>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Sent by</TableCell>
                <TableCell>Memo</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>WC Order</TableCell>
                {showCommentsAndActions && <TableCell>Comments</TableCell>}
                {showCommentsAndActions && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showCommentsAndActions ? 9 : 7} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                    No Zelle orders match the filters.
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatDate(row.emailDate ?? row.email_date ?? row.createdAt ?? row.created_at)}</TableCell>
                    <TableCell align="right">{formatAmount(row.amountCents ?? row.amount_cents)}</TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>{(row.sentBy ?? row.sent_by ?? '—').toString().slice(0, 30)}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>{(row.zelleMemo ?? row.zelle_memo ?? '—').toString()}</TableCell>
                    <TableCell>{row.source ?? '—'}</TableCell>
                    <TableCell>{row.status ?? '—'}</TableCell>
                    <TableCell>
                      {(row.wooOrderId ?? row.woo_order_id) ? (
                        <Button
                          size="small"
                          onClick={() => openWooOrderEdit(row.wooOrderId ?? row.woo_order_id)}
                        >
                          #{row.wooOrderId ?? row.woo_order_id}
                        </Button>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    {showCommentsAndActions && (
                      <TableCell sx={{ maxWidth: 280 }}>
                        {(row.comments || '').trim() ? (
                          <Stack spacing={0.75}>
                            <Typography variant="body2" sx={{ color: '#b26a00' }}>
                              {row.comments}
                            </Typography>
                            {extractCommentOrderIds(row).length > 0 && (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {extractCommentOrderIds(row).map((cid) => (
                                  <Button
                                    key={`comment-order-${row.id}-${cid}`}
                                    size="small"
                                    sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                                    onClick={() => openWooOrderEdit(cid)}
                                  >
                                    #{cid}
                                  </Button>
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        ) : '—'}
                      </TableCell>
                    )}
                    {showCommentsAndActions && (
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                          <IconButton size="small" title="View details" onClick={() => openView(row)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                          <IconButton size="small" title="Edit (status & order ID)" onClick={() => openEditDialog(row)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          {canDeleteRow(row) && (
                            <IconButton
                              size="small"
                              title="Delete"
                              onClick={() => handleDeleteRow(row)}
                              disabled={deletingId === row.id}
                            >
                              {deletingId === row.id ? <CircularProgress size={14} /> : <DeleteOutline fontSize="small" />}
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {(page > 0 || hasNextPage || total > limit) && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Page {page + 1} (showing up to {limit})
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button size="small" disabled={!hasNextPage} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </Stack>
            </Stack>
          )}
        </Paper>
      )}

      <Dialog
        open={duplicateDialogOpen}
        onClose={handleCloseDuplicateDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: 'min(1440px, calc(100vw - 48px))',
          },
        }}
      >
        <DialogTitle>Duplicate Orders</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Duplicate orders are grouped by the exact shared fields: <strong>`zelle_sent_by`</strong>, <strong>`zelle_email_date`</strong>, and <strong>`total_str`</strong>. Keep the first/original order and review the later duplicate before trashing it.
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {duplicateRows.length} duplicate order{duplicateRows.length === 1 ? '' : 's'} found
            </Typography>
            <Button size="small" startIcon={duplicateLoading ? <CircularProgress size={14} /> : <Refresh />} onClick={loadDuplicateOrders} disabled={duplicateLoading || !!duplicateDeletingOrderId}>
              Refresh
            </Button>
          </Stack>
          {duplicateError ? <Alert severity="error" sx={{ mb: 2 }}>{duplicateError}</Alert> : null}
          {duplicateLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper sx={{ overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Sent by</TableCell>
                    <TableCell>Zelle email date</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Original order</TableCell>
                    <TableCell>Original created</TableCell>
                    <TableCell>Duplicate order</TableCell>
                    <TableCell>Duplicate created</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {duplicateRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No duplicate orders found.
                      </TableCell>
                    </TableRow>
                  ) : duplicateRows.map((row) => {
                    const duplicateOrderId = Number(row.duplicate_order_id);
                    const originalOrderId = Number(row.original_order_id);
                    return (
                      <TableRow key={`duplicate-order-${duplicateOrderId}`}>
                        <TableCell sx={{ minWidth: 200 }}>{row.zelle_sent_by || '—'}</TableCell>
                        <TableCell sx={{ minWidth: 170 }}>{formatDate(row.zelle_email_date)}</TableCell>
                        <TableCell sx={{ minWidth: 90 }}>{row.total_str ? `$${row.total_str}` : '—'}</TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Button size="small" onClick={() => openWooOrderEdit(originalOrderId)}>
                              #{originalOrderId}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              {row.original_customer_name || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.original_item_summary || '—'}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 170 }}>{formatDate(row.original_created_at)}</TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Button size="small" color="warning" onClick={() => openWooOrderEdit(duplicateOrderId)}>
                              #{duplicateOrderId}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              {row.duplicate_customer_name || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.duplicate_item_summary || '—'}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 170 }}>{formatDate(row.duplicate_created_at)}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={!canDeleteOrders || duplicateDeletingOrderId === duplicateOrderId}
                            onClick={() => handleDeleteDuplicateOrder(row)}
                          >
                            {duplicateDeletingOrderId === duplicateOrderId ? 'Deleting…' : 'Delete duplicate'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDuplicateDialog} disabled={!!duplicateDeletingOrderId}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!duplicateDeleteCandidate} onClose={handleCloseDuplicateDeleteConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Duplicate Order Delete</DialogTitle>
        <DialogContent>
          {duplicateDeleteCandidate && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                This will cancel duplicate order #{duplicateDeleteCandidate.duplicate_order_id}. Original order #{duplicateDeleteCandidate.original_order_id} will be kept. A private note will be added so it can be permanently removed later.
              </Alert>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Typography variant="body2"><strong>Sent by:</strong> {duplicateDeleteCandidate.zelle_sent_by || '—'}</Typography>
                  <Typography variant="body2"><strong>Zelle email date:</strong> {formatDate(duplicateDeleteCandidate.zelle_email_date)}</Typography>
                  <Typography variant="body2"><strong>Total:</strong> {duplicateDeleteCandidate.total_str ? `$${duplicateDeleteCandidate.total_str}` : '—'}</Typography>
                  <Typography variant="body2"><strong>Original order:</strong> #{duplicateDeleteCandidate.original_order_id} created {formatDate(duplicateDeleteCandidate.original_created_at)}</Typography>
                  <Typography variant="body2"><strong>Original customer:</strong> {duplicateDeleteCandidate.original_customer_name || '—'}</Typography>
                  <Typography variant="body2"><strong>Original items:</strong> {duplicateDeleteCandidate.original_item_summary || '—'}</Typography>
                  <Typography variant="body2"><strong>Duplicate order:</strong> #{duplicateDeleteCandidate.duplicate_order_id} created {formatDate(duplicateDeleteCandidate.duplicate_created_at)}</Typography>
                  <Typography variant="body2"><strong>Duplicate customer:</strong> {duplicateDeleteCandidate.duplicate_customer_name || '—'}</Typography>
                  <Typography variant="body2"><strong>Duplicate items:</strong> {duplicateDeleteCandidate.duplicate_item_summary || '—'}</Typography>
                </Stack>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDuplicateDeleteConfirm} disabled={!!duplicateDeletingOrderId}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!!duplicateDeletingOrderId}
            onClick={handleConfirmDeleteDuplicateOrder}
          >
            {duplicateDeletingOrderId ? 'Cancelling…' : 'Cancel duplicate order'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editRow} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Zelle payment</DialogTitle>
        <DialogContent>
          {editRow && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {editError ? <Alert severity="error">{editError}</Alert> : null}
              {editSuccess ? <Alert severity="success">{editSuccess}</Alert> : null}
              <TextField
                size="small"
                label="Date"
                value={formatDate(editRow.emailDate ?? editRow.email_date ?? editRow.createdAt ?? editRow.created_at)}
                InputProps={{ readOnly: true }}
              />
              <TextField
                size="small"
                label="Sent by"
                value={editRow.sentBy ?? editRow.sent_by ?? ''}
                InputProps={{ readOnly: true }}
              />
              <TextField
                size="small"
                label="Amount"
                value={formatAmount(editRow.amountCents ?? editRow.amount_cents)}
                InputProps={{ readOnly: true }}
              />
              <TextField
                size="small"
                label="Memo"
                value={editRow.zelleMemo ?? editRow.zelle_memo ?? ''}
                InputProps={{ readOnly: true }}
                multiline
                minRows={2}
              />
              <TextField
                size="small"
                label="Source"
                value={editRow.source ?? ''}
                InputProps={{ readOnly: true }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editForm.status}
                  label="Status"
                  onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="WC Order ID"
                value={editForm.woo_order_id}
                onChange={(e) => setEditForm((f) => ({ ...f, woo_order_id: e.target.value }))}
                placeholder="Optional"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={editLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={18} /> : <Edit />}
          >
            {editLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={!!viewRow} onClose={closeView} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Zelle payment details</Typography>
            <IconButton onClick={closeView}><Close /></IconButton>
          </Stack>
          {viewRow && (
            <Stack spacing={1.5}>
              <Row label="ID" value={viewRow.id} />
              <Row label="Gmail message ID" value={viewRow.gmailMessageId ?? viewRow.gmail_message_id} />
              <Row label="Date" value={formatDate(viewRow.emailDate ?? viewRow.email_date ?? viewRow.createdAt ?? viewRow.created_at)} />
              <Row label="Amount" value={formatAmount(viewRow.amountCents ?? viewRow.amount_cents)} />
              <Row label="Sent by" value={viewRow.sentBy ?? viewRow.sent_by} />
              <Row label="Memo" value={viewRow.zelleMemo ?? viewRow.zelle_memo} />
              <Row label="Status" value={viewRow.status} />
              <Row label="Source" value={viewRow.source} />
              {(viewRow.errorMessage ?? viewRow.error_message) && (
                <Row label="Error" value={viewRow.errorMessage ?? viewRow.error_message} />
              )}
              {(viewRow.wooOrderId ?? viewRow.woo_order_id) && (
                <Box>
                  <Typography variant="caption" color="text.secondary">WooCommerce order</Typography>
                  <Typography variant="body2">
                    <Button
                      size="small"
                      onClick={() => {
                        const id = viewRow.wooOrderId ?? viewRow.woo_order_id;
                        closeView();
                        if (id != null && id !== '') openWooOrderEdit(id);
                      }}
                    >
                      View order #{viewRow.wooOrderId ?? viewRow.woo_order_id}
                    </Button>
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Drawer>

    </>
  );
}

function Row({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{value ?? '—'}</Typography>
    </Box>
  );
}
